import { useState, useEffect, useCallback } from "react";
import api from "../../services/api";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from "recharts";
import ProtectedLink from "../../components/ProtectedLink";

// Même garde-fou que Budget.jsx (onglets Scénarios/Comparaison) : le claim
// JWT `plan` n'existe plus dans aucun token émis depuis le passage au modèle
// socle + modules, donc planRank retombe systématiquement à 0 pour tout le
// monde. Tant que SAAS_MODE est désactivé (pas de REACT_APP_SAAS_MODE=true),
// ce verrou ne doit pas bloquer l'onglet.
const SAAS_MODE = process.env.REACT_APP_SAAS_MODE === 'true';

const MONTHS     = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
const MONTH_ABBR = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];

const ENC_CATS = ['encaissement_clients','autre_encaissement'];
const DEC_CATS = ['décaissement_fournisseurs','charges_personnel','charges_fixes','investissement','remboursement_emprunt','tva_à_payer','is_à_payer','dividendes','autre_décaissement'];

const CAT_LABELS = {
  encaissement_clients:         "Clients à encaisser",
  autre_encaissement:           "Autres encaissements",
  décaissement_fournisseurs:    "Fournisseurs à payer",
  charges_personnel:            "Charges personnel",
  charges_fixes:                "Charges fixes / loyers",
  investissement:               "Investissements",
  remboursement_emprunt:        "Remboursements emprunts",
  tva_à_payer:                  "TVA à payer",
  is_à_payer:                   "IS à payer",
  dividendes:                   "Dividendes",
  autre_décaissement:           "Autres décaissements",
};

function r2(n) { return Math.round((n || 0) * 100) / 100; }

export default function CashForecastTab({ scenarioId, currentCompanyId, planRank }) {
  const [lines, setLines]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [dirty, setDirty]   = useState({});
  const [msg, setMsg]       = useState("");
  const [startBalance, setStartBalance] = useState(0);
  const [alertThreshold, setAlertThreshold] = useState(0);

  const load = useCallback(() => {
    if (!scenarioId) return;
    setLoading(true);
    api.budget.cash(scenarioId).then(setLines).catch(console.error).finally(() => setLoading(false));
  }, [scenarioId]);

  useEffect(() => { load(); }, [load]);

  const syncCash = async () => {
    setSyncing(true);
    try {
      const r = await api.budget.syncCash(scenarioId, { company_id: currentCompanyId });
      setMsg("✓ " + r.message);
      load();
    } catch (e) { setMsg("Erreur : " + e.message); }
    finally { setSyncing(false); setTimeout(() => setMsg(""), 4000); }
  };

  const getVal = (line, month) => {
    const d = dirty[line.id];
    if (d?.[month] !== undefined) return d[month];
    return line[month] || 0;
  };

  const commitEdit = () => {
    if (!editing) return;
    const { lineId, month } = editing;
    const parsed = parseFloat(editVal) || 0;
    setDirty(prev => ({ ...prev, [lineId]: { ...(prev[lineId] || {}), [month]: parsed } }));
    setEditing(null);
  };

  const save = async () => {
    setSaving(true);
    const payload = lines.map(l => {
      const changes = dirty[l.id] || {};
      return { ...l, ...MONTHS.reduce((acc, m) => ({ ...acc, [m]: changes[m] !== undefined ? changes[m] : (l[m] || 0) }), {}) };
    });
    try {
      await api.budget.saveCash(scenarioId, { lines: payload });
      setMsg("✓ Enregistré");
      setDirty({});
      load();
    } catch (e) { setMsg("Erreur : " + e.message); }
    finally { setSaving(false); setTimeout(() => setMsg(""), 3000); }
  };

  const addLine = async (category) => {
    const label = prompt(`Libellé pour "${CAT_LABELS[category]}" :`, "Nouvelle ligne");
    if (!label) return;
    const maxOrd = lines.filter(l => l.category === category).reduce((m, l) => Math.max(m, l.sort_order || 0), 0);
    try {
      await api.budget.saveCash(scenarioId, { lines: [{ category, label, sort_order: maxOrd + 10, ...Object.fromEntries(MONTHS.map(m => [m, 0])) }] });
      load();
    } catch (e) { setMsg("Erreur : " + e.message); }
  };

  const deleteLine = async (line) => {
    if (!window.confirm(`Supprimer "${line.label}" ?`)) return;
    try { await api.budget.deleteCashLine(scenarioId, line.id); load(); }
    catch (e) { setMsg("Erreur : " + e.message); }
  };

  // Compute monthly sums
  const monthSum = (categories, monthKey) => {
    return r2(lines.filter(l => categories.includes(l.category)).reduce((s, l) => s + (getVal(l, monthKey) || 0), 0));
  };
  const encMonthly = (m) => monthSum(ENC_CATS, m);
  const decMonthly = (m) => monthSum(DEC_CATS, m);
  const soldeMonthly = (m) => r2(encMonthly(m) - decMonthly(m));

  // Cumulative from start balance
  const chartData = MONTHS.map((m, i) => {
    let cumul = r2((parseFloat(startBalance) || 0) + MONTHS.slice(0, i + 1).reduce((s, mm) => s + soldeMonthly(mm), 0));
    return { name: MONTH_ABBR[i], encaissements: encMonthly(m), décaissements: decMonthly(m), solde: soldeMonthly(m), cumulatif: cumul };
  });

  const threshold = parseFloat(alertThreshold) || 0;
  const negativeMonths = chartData.filter(d => d.cumulatif < 0).length;
  const alertMonths    = chartData.filter(d => d.cumulatif < threshold && d.cumulatif >= 0).length;

  const inputSt = { padding: "6px 10px", border: "1px solid var(--border-color,#e2e8f0)", borderRadius: 8, fontSize: 12, background: "var(--input-bg,#fff)", color: "var(--text,#1f2937)" };
  const cellSt = (active) => ({ padding: "4px 2px", borderRight: "1px solid #f3f4f6", background: active ? "#eff6ff" : "transparent", cursor: "text", minWidth: 72 });

  if (!scenarioId) return <div style={{ textAlign: "center", padding: 60, color: "var(--text-muted)", fontSize: 13 }}>Sélectionnez un scénario</div>;
  if (planRank < 1 && SAAS_MODE) return (
    <div style={{ textAlign: "center", padding: 80 }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
      <div style={{ fontSize: 16, fontWeight: 600 }}>Prévisionnel trésorerie — Plan TPE requis</div>
    </div>
  );

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Solde de départ :</span>
          <input type="number" value={startBalance} onChange={e => setStartBalance(e.target.value)} style={{ ...inputSt, width: 110 }} />
          <span style={{ fontSize: 11, color: "var(--text-muted)" }}>€</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Seuil alerte :</span>
          <input type="number" value={alertThreshold} onChange={e => setAlertThreshold(e.target.value)} style={{ ...inputSt, width: 90 }} />
          <span style={{ fontSize: 11, color: "var(--text-muted)" }}>€</span>
        </div>
        <div style={{ flex: 1 }} />
        {msg && <span style={{ fontSize: 12, color: msg.startsWith("✓") ? "#16a34a" : "#dc2626" }}>{msg}</span>}
        {negativeMonths > 0 && <span style={{ padding: "4px 10px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, color: "#dc2626", fontSize: 12, fontWeight: 600 }}>⚠️ {negativeMonths} mois en négatif</span>}
        {alertMonths > 0 && <span style={{ padding: "4px 10px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, color: "#d97706", fontSize: 12, fontWeight: 600 }}>🔔 {alertMonths} mois sous le seuil</span>}
        <button onClick={syncCash} disabled={syncing} style={{ padding: "7px 13px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
          {syncing ? "Sync..." : "🔄 Pré-remplir depuis modules"}
        </button>
        <button onClick={save} disabled={saving} style={{ padding: "7px 13px", background: "#1a9e72", color: "#fff", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
          {saving ? "Enreg..." : "💾 Enregistrer"}
        </button>
        <ProtectedLink url={api.budget.exportCashUrl(scenarioId)} style={{ padding: "7px 13px", background: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12, textDecoration: "none", color: "var(--text,#1f2937)" }}>⬇ Excel</ProtectedLink>
      </div>

      {/* Chart */}
      <div style={{ background: "var(--card-bg,#fff)", border: "1px solid var(--border-color,#e2e8f0)", borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Solde cumulé prévisionnel</div>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="cash-grad-pos" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="cash-grad-neg" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor="#ef4444" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#ef4444" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
            <Tooltip formatter={(v, n) => [`${v.toLocaleString("fr-FR")} €`, n]} />
            <ReferenceLine y={0} stroke="#ef4444" strokeDasharray="4 2" strokeWidth={2} />
            {threshold > 0 && <ReferenceLine y={threshold} stroke="#f59e0b" strokeDasharray="4 2" label={{ value: `Seuil ${(threshold/1000).toFixed(0)}k€`, fill: "#d97706", fontSize: 10 }} />}
            <Area type="monotone" dataKey="cumulatif" stroke="#3b82f6" fill="url(#cash-grad-pos)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Table */}
      {loading ? <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>Chargement...</div> : (
        <div style={{ overflowX: "auto", background: "var(--card-bg,#fff)", border: "1px solid var(--border-color,#e2e8f0)", borderRadius: 12 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#f9fafb" }}>
                <th style={{ padding: "8px 12px", textAlign: "left", minWidth: 200, borderRight: "1px solid #e5e7eb", fontWeight: 600, color: "#6b7280", fontSize: 11 }}>Libellé</th>
                {MONTH_ABBR.map((m, i) => (
                  <th key={i} style={{ padding: "8px 4px", textAlign: "right", minWidth: 70, borderRight: "1px solid #e5e7eb", fontWeight: 600, color: "#6b7280", fontSize: 11 }}>{m}</th>
                ))}
                <th style={{ padding: "8px 8px", textAlign: "right", minWidth: 80, fontWeight: 700, color: "#1f2937", fontSize: 11, background: "#f0f0f0" }}>Total</th>
                <th style={{ width: 28 }} />
              </tr>
            </thead>
            <tbody>
              {/* Encaissements */}
              <tr style={{ background: "#f0fdf4", borderTop: "2px solid #e5e7eb" }}>
                <td colSpan={MONTHS.length + 3} style={{ padding: "7px 12px", fontWeight: 700, color: "#16a34a", fontSize: 12 }}>ENCAISSEMENTS</td>
              </tr>
              {lines.filter(l => ENC_CATS.includes(l.category)).map(line => (
                <tr key={line.id} style={{ borderBottom: "1px solid #f3f4f6", background: dirty[line.id] ? "#fffbeb" : "transparent" }}>
                  <td style={{ padding: "4px 12px", borderRight: "1px solid #f3f4f6" }}>
                    <div>{line.label}</div>
                    {line.is_auto && <span style={{ fontSize: 9, color: "#3b82f6", background: "#eff6ff", padding: "1px 5px", borderRadius: 5 }}>AUTO</span>}
                  </td>
                  {MONTHS.map((m, i) => {
                    const isActive = editing?.lineId === line.id && editing?.month === m;
                    const val = getVal(line, m);
                    return (
                      <td key={i} style={cellSt(isActive)} onClick={() => { if (editing) commitEdit(); setEditing({ lineId: line.id, month: m }); setEditVal(String(val || "")); }}>
                        {isActive
                          ? <input autoFocus type="number" step="1" style={{ width: "100%", border: "none", background: "transparent", textAlign: "right", fontSize: 12, padding: "3px 4px", outline: "none" }}
                              value={editVal} onChange={e => setEditVal(e.target.value)} onBlur={commitEdit} onKeyDown={e => { if (e.key === "Enter") commitEdit(); }} />
                          : <div style={{ textAlign: "right", padding: "3px 4px", color: val > 0 ? "#16a34a" : "#d1d5db" }}>{val > 0 ? val.toLocaleString("fr-FR", { maximumFractionDigits: 0 }) : "—"}</div>}
                      </td>
                    );
                  })}
                  <td style={{ padding: "4px 8px", textAlign: "right", fontWeight: 600, background: "#f9fafb", color: "#16a34a" }}>
                    {r2(MONTHS.reduce((s, m) => s + (getVal(line, m) || 0), 0)).toLocaleString("fr-FR", { maximumFractionDigits: 0 })}
                  </td>
                  <td style={{ padding: "2px 4px" }}>
                    <button onClick={() => deleteLine(line)} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 13 }}>×</button>
                  </td>
                </tr>
              ))}
              {ENC_CATS.map(cat => (
                <tr key={`add-enc-${cat}`}>
                  <td colSpan={MONTHS.length + 3} style={{ padding: "4px 12px" }}>
                    <button onClick={() => addLine(cat)} style={{ fontSize: 11, color: "#3b82f6", background: "none", border: "none", cursor: "pointer" }}>+ {CAT_LABELS[cat]}</button>
                  </td>
                </tr>
              ))}

              {/* Total encaissements */}
              <tr style={{ background: "#dcfce7", borderTop: "1px solid #bbf7d0" }}>
                <td style={{ padding: "7px 12px", fontWeight: 700, fontSize: 12, borderRight: "1px solid #f3f4f6" }}>Total encaissements</td>
                {MONTHS.map((m, i) => (
                  <td key={i} style={{ padding: "7px 4px", textAlign: "right", fontWeight: 700, color: "#16a34a", borderRight: "1px solid #f3f4f6" }}>{encMonthly(m).toLocaleString("fr-FR", { maximumFractionDigits: 0 })}</td>
                ))}
                <td style={{ padding: "7px 8px", textAlign: "right", fontWeight: 700, color: "#16a34a", background: "#d1fae5" }}>
                  {r2(MONTHS.reduce((s, m) => s + encMonthly(m), 0)).toLocaleString("fr-FR", { maximumFractionDigits: 0 })}
                </td>
                <td />
              </tr>

              {/* Décaissements */}
              <tr style={{ background: "#fef2f2", borderTop: "2px solid #e5e7eb" }}>
                <td colSpan={MONTHS.length + 3} style={{ padding: "7px 12px", fontWeight: 700, color: "#dc2626", fontSize: 12 }}>DÉCAISSEMENTS</td>
              </tr>
              {lines.filter(l => DEC_CATS.includes(l.category)).map(line => (
                <tr key={line.id} style={{ borderBottom: "1px solid #f3f4f6", background: dirty[line.id] ? "#fffbeb" : "transparent" }}>
                  <td style={{ padding: "4px 12px", borderRight: "1px solid #f3f4f6" }}>
                    <div>{line.label}</div>
                    {line.is_auto && <span style={{ fontSize: 9, color: "#3b82f6", background: "#eff6ff", padding: "1px 5px", borderRadius: 5 }}>AUTO</span>}
                  </td>
                  {MONTHS.map((m, i) => {
                    const isActive = editing?.lineId === line.id && editing?.month === m;
                    const val = getVal(line, m);
                    return (
                      <td key={i} style={cellSt(isActive)} onClick={() => { if (editing) commitEdit(); setEditing({ lineId: line.id, month: m }); setEditVal(String(val || "")); }}>
                        {isActive
                          ? <input autoFocus type="number" step="1" style={{ width: "100%", border: "none", background: "transparent", textAlign: "right", fontSize: 12, padding: "3px 4px", outline: "none" }}
                              value={editVal} onChange={e => setEditVal(e.target.value)} onBlur={commitEdit} onKeyDown={e => { if (e.key === "Enter") commitEdit(); }} />
                          : <div style={{ textAlign: "right", padding: "3px 4px", color: val > 0 ? "#dc2626" : "#d1d5db" }}>{val > 0 ? val.toLocaleString("fr-FR", { maximumFractionDigits: 0 }) : "—"}</div>}
                      </td>
                    );
                  })}
                  <td style={{ padding: "4px 8px", textAlign: "right", fontWeight: 600, background: "#f9fafb", color: "#dc2626" }}>
                    {r2(MONTHS.reduce((s, m) => s + (getVal(line, m) || 0), 0)).toLocaleString("fr-FR", { maximumFractionDigits: 0 })}
                  </td>
                  <td style={{ padding: "2px 4px" }}>
                    <button onClick={() => deleteLine(line)} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 13 }}>×</button>
                  </td>
                </tr>
              ))}
              {DEC_CATS.map(cat => (
                <tr key={`add-dec-${cat}`}>
                  <td colSpan={MONTHS.length + 3} style={{ padding: "4px 12px" }}>
                    <button onClick={() => addLine(cat)} style={{ fontSize: 11, color: "#dc2626", background: "none", border: "none", cursor: "pointer" }}>+ {CAT_LABELS[cat]}</button>
                  </td>
                </tr>
              ))}

              {/* Total décaissements */}
              <tr style={{ background: "#fee2e2", borderTop: "1px solid #fecaca" }}>
                <td style={{ padding: "7px 12px", fontWeight: 700, fontSize: 12, borderRight: "1px solid #f3f4f6" }}>Total décaissements</td>
                {MONTHS.map((m, i) => (
                  <td key={i} style={{ padding: "7px 4px", textAlign: "right", fontWeight: 700, color: "#dc2626", borderRight: "1px solid #f3f4f6" }}>{decMonthly(m).toLocaleString("fr-FR", { maximumFractionDigits: 0 })}</td>
                ))}
                <td style={{ padding: "7px 8px", textAlign: "right", fontWeight: 700, color: "#dc2626", background: "#fecaca" }}>
                  {r2(MONTHS.reduce((s, m) => s + decMonthly(m), 0)).toLocaleString("fr-FR", { maximumFractionDigits: 0 })}
                </td>
                <td />
              </tr>

              {/* Solde mensuel */}
              <tr style={{ background: "#fffbeb", borderTop: "2px solid #f59e0b" }}>
                <td style={{ padding: "8px 12px", fontWeight: 700, fontSize: 12, borderRight: "1px solid #f3f4f6" }}>Solde mensuel</td>
                {MONTHS.map((m, i) => { const s = soldeMonthly(m); return (
                  <td key={i} style={{ padding: "8px 4px", textAlign: "right", fontWeight: 700, color: s >= 0 ? "#16a34a" : "#dc2626", borderRight: "1px solid #f3f4f6" }}>{s.toLocaleString("fr-FR", { maximumFractionDigits: 0 })}</td>
                ); })}
                <td style={{ padding: "8px 8px", textAlign: "right", fontWeight: 700, background: "#fef3c7", color: "#d97706" }}>
                  {r2(MONTHS.reduce((s, m) => s + soldeMonthly(m), 0)).toLocaleString("fr-FR", { maximumFractionDigits: 0 })}
                </td>
                <td />
              </tr>

              {/* Solde cumulé */}
              <tr style={{ background: "#eff6ff", borderTop: "1px solid #bfdbfe" }}>
                <td style={{ padding: "8px 12px", fontWeight: 700, fontSize: 12, borderRight: "1px solid #f3f4f6" }}>Solde cumulé</td>
                {chartData.map((d, i) => (
                  <td key={i} style={{ padding: "8px 4px", textAlign: "right", fontWeight: 700, color: d.cumulatif >= 0 ? "#3b82f6" : "#dc2626", borderRight: "1px solid #f3f4f6", background: d.cumulatif < 0 ? "#fef2f2" : "transparent" }}>
                    {d.cumulatif.toLocaleString("fr-FR", { maximumFractionDigits: 0 })}
                  </td>
                ))}
                <td style={{ background: "#dbeafe" }} />
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
